import React, { useContext, useEffect, useState } from "react";
import { cn, incPanelColorLevel } from "../../styles";
import { Typography } from "../../atoms";
import { PanelColor, ThemeColor } from "../../props";
import { Panel } from "../../layouts/Panel";
import { LocalizationScope } from "../../util/LocalizationScope";
import { DragProvider, DragCallbackParams, DragContext } from "../../util/DragProvider";
import { DragList } from "../../layouts/DragList";
import { DragItem } from "../DragItem";
import { SelectOption } from "../Select";
import { reorderArray } from "../../helper";
import { processErrorContent } from "./helper";
import { BaseFormFieldProps, FormFieldConstraint } from "./index";

export type OrderingFieldValue = SelectOption[];

export interface FormFieldOrderingProps extends BaseFormFieldProps {
  constraints: FormFieldConstraint;
  value: OrderingFieldValue | null;
  onChangeField: (id: string, value: OrderingFieldValue) => void;
  options: SelectOption[];
  color?: PanelColor;
  /**
   * Custom render function for an item's content.
   * When provided, overrides the default item rendering.
   */
  renderItem?: (item: SelectOption) => React.ReactNode;
}

function OrderingField({
  id,
  value,
  onChangeField,
  options,
  disabled,
  readOnly,
  dense,
  color = PanelColor.level1,
  renderItem,
}: Pick<FormFieldOrderingProps, "id" | "value" | "onChangeField" | "options" | "disabled" | "readOnly" | "dense" | "color" | "renderItem">) {
  const itemColor = incPanelColorLevel(color, 1) as PanelColor;
  const [items, setItems] = useState<SelectOption[]>(
    Array.isArray(value) ? value : Array.isArray(options) ? options : []
  );

  useEffect(() => {
    if (!value && Array.isArray(options)) {
      onChangeField(id, options);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (Array.isArray(value)) setItems(value);
  }, [value]);

  const handleChange = (params: DragCallbackParams<SelectOption>) => {
    if (params.type === "reorder") {
      const reordered = reorderArray(items, params.sourceIndex, params.destinationIndex);
      setItems(reordered);
      onChangeField(id, reordered);
    }
  };

  return (
    <Panel
      color={color}
      border
      className={cn(
        "p-2 flex-1 min-h-0 overflow-auto",
        dense ? "gap-1" : "gap-1.5",
      )}
    >
      <DragList<SelectOption>
        id={id}
        onDragListChange={handleChange}
        className="flex flex-col gap-1"
      >
        {items.map((item, index) => (
          <DragList.Item<SelectOption>
            key={item.value}
            id={item.value}
            index={index}
            data={item}
          >
            {({ handleProps, isDragging }) => (
              <DragItem
                handleProps={disabled || readOnly ? null : handleProps}
                isDragging={isDragging}
                dense={dense}
                color={itemColor}
              >
                {renderItem ? (
                  renderItem(item)
                ) : (
                  <Typography.Span className={cn(dense ? "text-xs" : "text-sm")}>
                    {typeof item.label === "string" ? item.label : ""}
                  </Typography.Span>
                )}
              </DragItem>
            )}
          </DragList.Item>
        ))}
      </DragList>
    </Panel>
  );
}

export const Ordering: React.FC<FormFieldOrderingProps> = ({
  className,
  id,
  value,
  onChangeField,
  label,
  localKey,
  errors = [],
  touched = false,
  helper = null,
  helperKey,
  constraints,
  disabled,
  keepHeight,
  noLabel,
  dense,
  options,
  color = PanelColor.level1,
  readOnly,
  renderItem,
}) => {
  const error = touched && errors[0];
  const hasDragContext = !!useContext(DragContext);

  const field = (
    <OrderingField
      id={id}
      value={value}
      onChangeField={onChangeField}
      options={options}
      disabled={disabled}
      readOnly={readOnly}
      dense={dense}
      color={color}
      renderItem={renderItem}
    />
  );

  return (
    <div className={className}>
      {!noLabel && (
        <div className="flex">
          <Typography.Label
            htmlFor={id}
            className={cn(
              "block font-medium select-none",
              dense ? "mb-0.5 text-xs" : "mb-1 text-sm"
            )}
            content={label ?? { local: id }}
          />
          {!!constraints.presence && (
            <span className="text-red-500 ml-0.5 text-xs align-super">*</span>
          )}
        </div>
      )}

      {hasDragContext ? field : <DragProvider>{field}</DragProvider>}

      {(helperKey || helper || keepHeight) && !error && (
        <LocalizationScope scope={localKey}>
          <Typography.P
            id={`${id}-helper`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.gray}
            content={helperKey ? { local: helperKey } : helper ? helper : null}
          >
            {" "}
          </Typography.P>
        </LocalizationScope>
      )}
      {error && (
        <LocalizationScope scope={[localKey, "::PSS"]}>
          <Typography.P
            id={`${id}-error`}
            className={cn("text-xs", dense ? "my-0.5" : "my-1")}
            color={ThemeColor.error}
            content={processErrorContent(error)}
          />
        </LocalizationScope>
      )}
    </div>
  );
};
